<template>
  <button
    @click="toggleDarkMode"
    class="dark-mode-toggle"
    :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
  >
    <div class="toggle-track">
      <div class="toggle-thumb" :class="{ dark: isDark }">
        <!-- Sun Icon (Light Mode) -->
        <svg v-if="!isDark" class="toggle-icon" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clip-rule="evenodd"
          />
        </svg>

        <!-- Moon Icon (Dark Mode) -->
        <svg v-else class="toggle-icon" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      </div>

      <!-- Background decorations -->
      <div class="toggle-bg-icons">
        <span class="sun-icon">☀️</span>
        <span class="moon-icon">🌙</span>
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'

const isDark = ref(false)

const toggleDarkMode = () => {
  isDark.value = !isDark.value
  updateTheme(isDark.value)
}

const updateTheme = (dark: boolean) => {
  const root = document.documentElement
  if (dark) {
    root.classList.add('app-dark')
    localStorage.setItem('hudstore-theme', 'dark')
  } else {
    root.classList.remove('app-dark')
    localStorage.setItem('hudstore-theme', 'light')
  }
}

// Load saved theme preference
onMounted(() => {
  const savedTheme = localStorage.getItem('hudstore-theme')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

  isDark.value = savedTheme === 'dark' || (!savedTheme && prefersDark)
  updateTheme(isDark.value)
})

// Watch for system theme changes
onMounted(() => {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  const handleChange = (e: MediaQueryListEvent) => {
    if (!localStorage.getItem('hudstore-theme')) {
      isDark.value = e.matches
      updateTheme(isDark.value)
    }
  }

  mediaQuery.addEventListener('change', handleChange)
  return () => mediaQuery.removeEventListener('change', handleChange)
})
</script>

<style scoped>
.dark-mode-toggle {
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 44px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-track {
  position: relative;
  width: 100%;
  height: 100%;
  /* Warm sunny gradient for light mode - like daylight */
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 9999px;
  transition: background 0.3s ease;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.app-dark .toggle-track {
  /* Cool dark gradient for dark mode - like night sky */
  background: linear-gradient(135deg, #1a1a1a 0%, #000000 100%);
}

.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

.toggle-thumb.dark {
  transform: translateX(20px);
}

.toggle-icon {
  width: 12px;
  height: 12px;
  /* Deep amber for sun icon - matches sunny toggle track */
  color: #b45309;
}

.toggle-thumb.dark .toggle-icon {
  color: var(--app-text-muted);
}

/* Background decoration icons */
.toggle-bg-icons {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5px;
  pointer-events: none;
}

.sun-icon,
.moon-icon {
  font-size: 10px;
  opacity: 0.3;
  transition: opacity 0.3s ease;
}

.app-dark .sun-icon {
  opacity: 0.1;
}

.app-dark .moon-icon {
  opacity: 0.6;
}

/* Hover effect */
.dark-mode-toggle:hover .toggle-track {
  filter: brightness(1.1);
}

.dark-mode-toggle:focus {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 2px;
  border-radius: 9999px;
}

/* Smooth animation */
@media (prefers-reduced-motion: reduce) {
  .toggle-thumb,
  .toggle-track {
    transition: none;
  }
}
</style>
